import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  GridSortModel,
  GridRowSelectionModel,
  GridPaginationModel,
  GridRowModesModel,
  GridRowModes,
  GridRowId,
  GridActionsCellItem,
  GridEventListener,
  GridRowEditStopReasons,
  GridValidRowModel,
  GridRenderCellParams,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridToolbarDensitySelector,
  gridClasses,
} from '@mui/x-data-grid';
import {
  Box,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Alert,
  AlertTitle,
  Typography,
  Chip,
  LinearProgress,
  Stack,
  useTheme,
  alpha,
  CircularProgress,
  TextField,
  Divider,
  Badge,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  ErrorOutline,
  DragIndicator as DragIndicatorIcon,
  GroupWork as GroupWorkIcon,
  Close as CloseIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
} from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { formatCurrencyNumber, formatDate, formatDateTime, formatIntPhoneNumber } from '../../helpers/formatting-utils';
import dayjs from 'dayjs';
import { colorParser } from './ColorParser';

// Helper function to detect and format ISO date strings
const detectAndFormatDate = (value: any): React.ReactNode | null => {
  if (!value || typeof value !== 'string') return null;
  
  // Check if it's an ISO date string pattern (YYYY-MM-DDTHH:mm:ss.sssZ or similar)
  const isoDatePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;
  
  if (isoDatePattern.test(value)) {
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        const dateStr = date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: '2-digit', 
          year: 'numeric' 
        });
        const timeStr = date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          second: '2-digit',
          hour12: false 
        });
        
        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {dateStr}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {timeStr}
            </Typography>
          </Box>
        );
      }
    } catch {
      return null;
    }
  }
  
  return null;
};

// Interfaces
export interface PaginatedDataGridColumn extends Omit<GridColDef, 'renderCell'> {
  renderCell?: (params: GridRenderCellParams) => React.ReactNode;
  exportable?: boolean;
  searchable?: boolean;
  required?: boolean;
  validation?: (value: any) => string | null;
  columnType?: 'color' | 'chip' | 'progress' | 'phone' | 'currency' | 'barcode';
  chipColors?: Record<string, { backgroundColor: string; color: string }>;
  progressConfig?: {
    expectedField: string;
    actualField: string;
    dueDateField?: string;
  };
  barcodeConfig?: {
    format?: string;
    width?: number;
    height?: number;
  };
  groupable?: boolean;
}

export interface GroupingField {
  field: string;
  headerName: string;
}

export interface GroupedDataRow {
  id: string;
  groupKey: string;
  groupValue: string;
  count: number;
  isGroup: true;
  level: number;
  path: string;
  expanded?: boolean;
  children?: any[];
  hasSubGroups?: boolean;
}

export interface GroupQueryOptions {
  GroupBy: string[];
  Level: number;
  GroupSortBy?: 'key' | 'count' | 'sum';
  GroupSortDescending?: boolean;
  IncludeCount?: boolean;
  ParentKey?: string;
  GroupPath?: string;
}

export interface PagedGroupResult {
  groups: Array<{
    key: string;
    value: string;
    count: number;
    level: number;
  }>;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface PaginatedDataGridFilter {
  id: string;
  label: string;
  type: 'select' | 'multiselect' | 'text' | 'number' | 'date' | 'daterange' | 'boolean';
  column: string;
  options?: Array<{ value: any; label: string }> | ((data: any[]) => Array<{ value: any; label: string }>);
  defaultValue?: any;
}

export interface PaginatedDataGridAction {
  key: string;
  title: string;
  icon?: React.ReactNode;
  onClick: (selectedIds: GridRowId[]) => void | Promise<void>;
  disabled?: (selectedIds: GridRowId[]) => boolean;
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  confirmDialog?: {
    title: string;
    message: string | ((count: number) => string);
  };
  visible?: (selectedIds: GridRowId[]) => boolean;
}

export interface BackendPaginationResponse {
  items?: any[];
  data?: any[];
  groups?: Array<{
    key: string;
    value: string;
    count: number;
    level: number;
  }>;
  totalCount: number;
  totalGroups?: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface DensityConfig {
  rowHeight: number;
  cellPadding: number;
  headerPadding: number;
  fontSize: number;
  iconSize: number;
  chipSize: 'small' | 'medium';
  spacing: number;
}

export interface PaginatedDataGridProps {
  title?: string;
  subtitle?: string;
  columns: PaginatedDataGridColumn[];
  rows?: any[];
  idField?: string;
  loading?: boolean;
  error?: Error | null;
  emptyMessage?: string;
  useBackendPagination?: boolean;
  apiEndpoint?: string;
  onDataFetch?: (params: {
    page: number;
    pageSize: number;
    sortModel?: GridSortModel;
    filterModel?: GridFilterModel;
    search?: string;
    grouping?: GroupQueryOptions;
  }) => Promise<BackendPaginationResponse>;
  cacheTimeout?: number;
  filters?: PaginatedDataGridFilter[];
  actions?: PaginatedDataGridAction[];
  rowActions?: Array<{
    key: string;
    icon: React.ReactNode;
    label: string;
    onClick: (id: GridRowId, row: any) => void;
    visible?: (row: any) => boolean;
    disabled?: (row: any) => boolean;
    color?: 'inherit' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  }>;
  onAddRow?: (row: any) => Promise<any>;
  onEditRow?: (row: any) => Promise<any>;
  onDeleteRow?: (id: GridRowId) => Promise<void>;
  addRowFields?: string[];
  wmsMode?: boolean;
  densityLevel?: 'ultra-compact' | 'compact' | 'standard' | 'comfortable' | 'spacious';
  customDensity?: Partial<DensityConfig>;
  striped?: boolean;
  collapsible?: boolean;
  maxHeight?: string | number;
  minHeight?: string | number;
  autoHeight?: boolean;
  hideFooterWhenEmpty?: boolean;
  compactEmptyState?: boolean;
  responsiveColumns?: boolean;
  onRowClick?: (params: any) => void;
  onSelectionChange?: (selection: GridRowId[]) => void;
  enableGrouping?: boolean;
  enableAggregation?: boolean;
  enablePivot?: boolean;
  initialState?: any;
  sx?: any;
  className?: string;
  groupedUrl?: string
  enableGroupDrillDown?: boolean; // New prop to enable group drill-down functionality
  fieldMapper?: (field: string) => string; // New prop to map field names for API calls
}

// Density configurations
const DENSITY_CONFIGS: Record<string, DensityConfig> = {
  'ultra-compact': {
    rowHeight: 32,
    cellPadding: 4,
    headerPadding: 8,
    fontSize: 0.75, // rem
    iconSize: 16,
    chipSize: 'small',
    spacing: 0.25,
  },
  'compact': {
    rowHeight: 37,
    cellPadding: 6,
    headerPadding: 10,
    fontSize: 0.8125, // rem  
    iconSize: 18,
    chipSize: 'small',
    spacing: 0.5,
  },
  'standard': {
    rowHeight: 42,
    cellPadding: 8,
    headerPadding: 12,
    fontSize: 0.875, // rem
    iconSize: 20,
    chipSize: 'small',
    spacing: 1,
  },
  'comfortable': {
    rowHeight: 53,
    cellPadding: 12,
    headerPadding: 16,
    fontSize: 0.9375, // rem
    iconSize: 22,
    chipSize: 'medium',
    spacing: 1.5,
  },
  'spacious': {
    rowHeight: 64,
    cellPadding: 16,
    headerPadding: 20,
    fontSize: 1, // rem
    iconSize: 24,
    chipSize: 'medium',
    spacing: 2,
  },
};

// Styles
const useStyles = makeStyles<{ density: DensityConfig }>()((theme, { density }) => ({
  root: {
    width: '100%',
    position: 'relative',
  },
  container: {
    width: '100%',
    background: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    overflowY: 'hidden',
    overflowX: 'auto',
  },
  wmsContainer: {
    backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff',
    border: `1px solid ${theme.palette.divider}`,
  },
  groupingZone: {
    minHeight: 48,
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f5f5f5',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  groupingZoneEmpty: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: 'transparent',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
  },
  groupingZoneDragOver: {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    border: `2px dashed ${theme.palette.primary.main}`,
  },
  groupChip: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
    },
  },
  draggableColumn: {
    cursor: 'grab',
    userSelect: 'none',
    '&:active': {
      cursor: 'grabbing',
    },
  },
  groupRowCell: {
    fontWeight: 600,
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
  header: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'default',
  },
  headerClickable: {
    cursor: 'pointer',
    userSelect: 'none',
  },
  titleSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  title: {
    fontSize: '1.2rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  subtitle: {
    fontSize: '0.95rem',
    color: theme.palette.text.secondary,
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  dataGrid: {
    border: 'none',
    fontSize: `${density.fontSize}rem`,
    width: '100%',
    overflowX: 'auto',
    '& .MuiDataGrid-root': {
      width: '100%',
      minWidth: 0,
    },
    '& .MuiDataGrid-main': {
      width: '100%',
      overflow: 'auto',
    },
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: theme.palette.background.paper,
      position: 'sticky',
      top: 0,
      zIndex: 2,
      width: '100%',
    },
    '& .MuiDataGrid-columnHeadersInner': {
      width: '100%',
    },
    '& .MuiDataGrid-virtualScroller': {
      width: '100%',
      overflow: 'auto',
    },
    '& .MuiDataGrid-virtualScrollerContent': {
      width: '100%',
    },
    '& .MuiDataGrid-row': {
      minHeight: `${density.rowHeight}px !important`,
      width: '100%',
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
      },
    },
    '& .MuiDataGrid-cell': {
      padding: `${density.cellPadding}px`,
      fontSize: `${density.fontSize}rem`,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      '&:focus': {
        outline: 'none',
      },
      '&:focus-within': {
        outline: 'none',
      },
    },
    '& .MuiDataGrid-columnHeader': {
      padding: `${density.headerPadding}px ${density.cellPadding}px`,
      fontSize: `${density.fontSize}rem`,
      fontWeight: 600,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      '&:focus': {
        outline: 'none',
      },
      '&:focus-within': {
        outline: 'none',
      },
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontSize: 'inherit',
      fontWeight: 'inherit',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    },
    '& .MuiSvgIcon-root': {
      fontSize: `${density.iconSize}px`,
    },
    '& .MuiDataGrid-columnSeparator': {
      display: 'flex',
    },
  },
  wmsDataGrid: {
    '& .MuiDataGrid-row': {
      '&:nth-of-type(even)': {
        backgroundColor: theme.palette.mode === 'dark' ? '#ffffff05' : '#00000005',
      },
    },
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  errorState: {
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 280,
    padding: theme.spacing(4, 2),
    background: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(2),
  },
  compactEmptyState: {
    minHeight: 120,
    padding: theme.spacing(2, 1),
    margin: theme.spacing(1),
    '& .MuiSvgIcon-root': {
      fontSize: 40,
    },
    '& .MuiTypography-h5': {
      fontSize: '1.1rem',
      marginBottom: theme.spacing(1),
    },
    '& .MuiTypography-body2': {
      fontSize: '0.8rem',
      marginBottom: theme.spacing(1.5),
    },
  },
  responsiveContainer: {
    width: '100%',
    overflow: 'auto',
    [theme.breakpoints.down('sm')]: {
      '& .MuiDataGrid-columnHeader': {
        padding: `${Math.max(density.headerPadding - 4, 4)}px ${Math.max(density.cellPadding - 2, 2)}px`,
        fontSize: `${Math.max(density.fontSize - 0.0625, 0.75)}rem`,
      },
      '& .MuiDataGrid-cell': {
        padding: `${Math.max(density.cellPadding - 2, 2)}px`,
        fontSize: `${Math.max(density.fontSize - 0.0625, 0.75)}rem`,
      },
    },
  },
  adaptiveHeight: {
    '& .MuiDataGrid-main': {
      overflowY: 'visible',
      overflowX: 'auto',
    },
    '& .MuiDataGrid-virtualScroller': {
      overflowY: 'visible',
      overflowX: 'auto',
    },
    '& .MuiDataGrid-viewport': {
      overflowY: 'visible',
      overflowX: 'auto',
      maxHeight: 'none',
    },
  },
}));

// Grouping Zone Component
const GroupingZone: React.FC<{
  groupingFields: GroupingField[];
  availableColumns: PaginatedDataGridColumn[];
  onGroupingChange: (fields: GroupingField[]) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  isDragOver: boolean;
  density: DensityConfig;
}> = ({ groupingFields, availableColumns, onGroupingChange, onDragOver, onDrop, onDragLeave, isDragOver, density }) => {
  const { classes, cx } = useStyles({ density });
  const theme = useTheme();

  const handleRemoveGroup = (fieldToRemove: string) => {
    onGroupingChange(groupingFields.filter(field => field.field !== fieldToRemove));
  };

  const isEmpty = groupingFields.length === 0;

  return (
    <Box
      className={cx(
        classes.groupingZone,
        isEmpty && classes.groupingZoneEmpty,
        isDragOver && classes.groupingZoneDragOver
      )}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragLeave={onDragLeave}
    >
      {isEmpty ? (
        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupWorkIcon />
          Drag column headers here to group by
        </Typography>
      ) : (
        <>
          <GroupWorkIcon color="primary" />
          <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
            Group by:
          </Typography>
          {groupingFields.map((field, index) => (
            <React.Fragment key={field.field}>
              {index > 0 && (
                <Typography variant="body2" color="text.secondary">
                  then by
                </Typography>
              )}
              <Chip
                label={field.headerName}
                size="small"
                color="primary"
                variant="outlined"
                className={classes.groupChip}
                deleteIcon={<CloseIcon />}
                onDelete={() => handleRemoveGroup(field.field)}
                icon={<DragIndicatorIcon />}
              />
            </React.Fragment>
          ))}
        </>
      )}
    </Box>
  );
};

// Draggable Column Header Component
const DraggableColumnHeader: React.FC<{
  column: PaginatedDataGridColumn;
  children: React.ReactNode;
  density: DensityConfig;
}> = ({ column, children, density }) => {
  const { classes } = useStyles({ density });

  const handleDragStart = (e: React.DragEvent) => {
    if (!column.groupable) return;
    e.dataTransfer.setData('application/json', JSON.stringify({
      field: column.field,
      headerName: column.headerName || column.field
    }));
  };

  return (
    <Box
      className={column.groupable ? classes.draggableColumn : ''}
      draggable={column.groupable}
      onDragStart={handleDragStart}
      sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
    >
      {column.groupable && <DragIndicatorIcon sx={{ fontSize: 16, mr: 0.5, opacity: 0.5 }} />}
      {children}
    </Box>
  );
};

// Custom Toolbar with Search and Grouping
const CustomToolbar: React.FC<{
  actions?: PaginatedDataGridAction[];
  selectedRows: GridRowId[];
  onActionClick: (action: PaginatedDataGridAction) => void;
  onAddClick?: () => void;
  canAdd?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  hasGrouping?: boolean;
  onClearGrouping?: () => void;
}> = ({ actions, selectedRows, onActionClick, onAddClick, canAdd, searchValue, onSearchChange, hasGrouping, onClearGrouping }) => {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
      <Box sx={{ flexGrow: 1 }} />
      {onSearchChange && (
        <TextField
          variant="standard"
          placeholder="Search..."
          value={searchValue || ''}
          onChange={(e) => onSearchChange(e.target.value)}
          size="small"
          sx={{ mr: 2, minWidth: 200 }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
          }}
        />
      )}
      {hasGrouping && onClearGrouping && (
        <Button
          size="small"
          startIcon={<CloseIcon />}
          onClick={onClearGrouping}
          sx={{ ml: 1 }}
          color="secondary"
        >
          Clear Grouping
        </Button>
      )}
      {canAdd && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={onAddClick}
          sx={{ ml: 1 }}
        >
          Add Row
        </Button>
      )}
      {actions && (
        <>
          {actions
            .filter(action => !action.visible || action.visible(selectedRows))
            .map(action => (
              <Tooltip key={action.key} title={action.title}>
                <IconButton
                  size="small"
                  color={action.color || 'primary'}
                  onClick={() => onActionClick(action)}
                  disabled={action.disabled?.(selectedRows)}
                >
                  {action.icon}
                </IconButton>
              </Tooltip>
            ))}
        </>
      )}
    </GridToolbarContainer>
  );
};

// Main Component
const PaginatedDataGrid: React.FC<PaginatedDataGridProps> = ({
  title,
  subtitle,
  columns: userColumns,
  rows: localRows = [],
  idField = 'id',
  loading: externalLoading = false,
  error: externalError = null,
  emptyMessage = 'No data available',
  useBackendPagination = false,
  apiEndpoint,
  onDataFetch,
  cacheTimeout = 300000,
  filters = [],
  actions = [],
  rowActions = [],
  onAddRow,
  onEditRow,
  onDeleteRow,
  addRowFields,
  wmsMode = false,
  densityLevel = 'standard',
  customDensity,
  striped = false,
  collapsible = false,
  maxHeight,
  minHeight,
  autoHeight = true,
  hideFooterWhenEmpty = true,
  compactEmptyState = false,
  responsiveColumns = true,
  onRowClick,
  onSelectionChange,
  enableGrouping = false,
  enableGroupDrillDown = false,
  fieldMapper,
  initialState,
  sx,
  className,
  groupedUrl
}) => {
  const baseDensity = DENSITY_CONFIGS[densityLevel] || DENSITY_CONFIGS.standard;
  const density: DensityConfig = customDensity ? { ...baseDensity, ...customDensity } : baseDensity;
  
  const getMuiDensityLevel = (level: string): 'compact' | 'standard' | 'comfortable' => {
    switch (level) {
      case 'ultra-compact':
      case 'compact':
        return 'compact';
      case 'comfortable':
      case 'spacious':
        return 'comfortable';
      default:
        return 'standard';
    }
  };
  
  const { classes, cx } = useStyles({ density });
  const theme = useTheme();
  const dispatch = useAppDispatch();
  
  const [rows, setRows] = useState<any[]>(localRows);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [collapsed, setCollapsed] = useState(false);
  const [cache, setCache] = useState<Record<string, any>>({});
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  
  const [groupingFields, setGroupingFields] = useState<GroupingField[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [groupCache, setGroupCache] = useState<Record<string, any>>({});
  const [currentGroups, setCurrentGroups] = useState<GroupedDataRow[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showingGroupItems, setShowingGroupItems] = useState(false);
  const [currentGroupPath, setCurrentGroupPath] = useState<string | null>(null);
  const [groupPaginationModel, setGroupPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [groupTotalRowCount, setGroupTotalRowCount] = useState(0);
  const [currentGroupLevel, setCurrentGroupLevel] = useState(0);
  const [groupNavigationHistory, setGroupNavigationHistory] = useState<string[]>([]);
  
  const storageKey = `datagrid-${title?.replace(/\s+/g, '-').toLowerCase()}-state`;
  const [savedState, setSavedState] = useLocalStorage(storageKey, {});
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchValue]);
  
  const getCacheKey = useCallback((params: any) => {
    return JSON.stringify(params);
  }, []);
  
  const handleGroupingChange = useCallback((fields: GroupingField[], preserveContext: boolean = false) => {
    setGroupingFields(fields);
    setExpandedGroups(new Set());
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    setGroupPaginationModel(prev => ({ ...prev, page: 0 }));
    setCache({});

    if (!preserveContext) {
      // Reset context when starting fresh grouping
      setCurrentGroupLevel(0);
      setGroupNavigationHistory([]);
      setShowingGroupItems(false);
      setCurrentGroupPath(null);
    } else {
      // When adding grouping within a context, switch to showing groups but preserve path
      setShowingGroupItems(false);
      // Keep currentGroupPath, currentGroupLevel, and groupNavigationHistory
    }
  }, []);

  
const restoreGroupsSnapshot = useCallback(() => {
  const snap = lastGroupsSnapshotRef.current;
  if (!snap) return false;

  setCurrentGroups(snap.groups);
  setGroupPaginationModel(snap.groupPaginationModel);
  setGroupTotalRowCount(snap.groupTotalRowCount);
  setCurrentGroupLevel(snap.currentGroupLevel);
  setGroupNavigationHistory(snap.groupNavigationHistory);

  setShowingGroupItems(false);
  setCurrentGroupPath(null);
  setRows([]);
  return true;
}, []);
  
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);
  
  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);
  
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      const { field, headerName } = data;

      if (!groupingFields.some(gf => gf.field === field)) {
        const newFields = [...groupingFields, { field, headerName }];
        // Determine if we should preserve context (adding grouping while in a group)
        const preserveContext = showingGroupItems || currentGroupPath !== null;
        handleGroupingChange(newFields, preserveContext);
      }
    } catch (error) {
      console.warn('Invalid drag data');
    }
  }, [groupingFields, handleGroupingChange, showingGroupItems, currentGroupPath]);
  
const handleClearGrouping = useCallback(() => {
  setGroupingFields([]);
  setExpandedGroups(new Set());
  setCurrentGroups([]);
  setCache({});

  if (enableGroupDrillDown && (showingGroupItems || currentGroupLevel > 0)) {
    if (restoreGroupsSnapshot()) return;
  }

  if (useBackendPagination) {
    fetchDataRef.current?.();
  } else {
    setRows(localRows);
  }
}, [
  enableGroupDrillDown,
  showingGroupItems,
  currentGroupLevel,
  useBackendPagination,
  localRows,
  restoreGroupsSnapshot,
]);


  const fetchGroupItems = useCallback(async (groupedUrl: string, groupPath: string) => {
    if ((!apiEndpoint && !onDataFetch) || !mountedRef.current) {
      console.log('Early return - missing apiEndpoint/onDataFetch or component unmounted');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      let items: any[] = [];
      let totalCount = 0;
      
      if (onDataFetch) {
        const encodedPath = encodeURIComponent(groupPath);
        const pageNumber = paginationModel.page + 1;
        const queryParams = new URLSearchParams({
          PageNumber: String(pageNumber),
          PageSize: String(paginationModel.pageSize || 25),
        });
        
        const url = `${groupedUrl}${encodedPath}?${queryParams.toString()}`;
        console.log('Fetching group items from URL:', url);
        console.log('Using page number:', pageNumber, 'from paginationModel.page:', paginationModel.page);
        
        const response = await DataService.get(url);
        console.log('Response status:', response.status);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to fetch group items`);
        }
        
        const jsonResponse = await response.json();
        console.log('Response data:', jsonResponse);
        
        if (jsonResponse?.data?.items) {
          items = Array.isArray(jsonResponse.data.items) ? jsonResponse.data.items : [];
        } else if (jsonResponse?.items) {
          items = Array.isArray(jsonResponse.items) ? jsonResponse.items : [];
        }
        
        totalCount = jsonResponse?.data?.totalCount || jsonResponse?.totalCount || items.length;
        
      } else if (apiEndpoint) {
        // Use direct API endpoint
        console.log('Using direct API endpoint');
        const encodedPath = encodeURIComponent(groupPath);
        const pageNumber = paginationModel.page + 1;
        const queryParams = new URLSearchParams({
          PageNumber: String(pageNumber),
          PageSize: String(paginationModel.pageSize || 25),
        });

        const url = `${apiEndpoint}/grouped/${encodedPath}?${queryParams.toString()}`;
        console.log('Fetching from URL:', url, 'Page:', pageNumber);
        
        const response = await DataService.get(url);
        console.log('Response status:', response.status);
        
        if (!response.ok) {
          throw new Error('Failed to fetch group items');
        }
        
        const jsonResponse = await response.json();
        console.log('Response data:', jsonResponse);
        
        // Handle nested response structure
        if (jsonResponse?.success && jsonResponse?.data?.items) {
          items = Array.isArray(jsonResponse.data.items) ? jsonResponse.data.items : [];
        } else if (jsonResponse?.items) {
          items = Array.isArray(jsonResponse.items) ? jsonResponse.items : [];
        }
        
        totalCount = jsonResponse?.totalCount || items.length;
      }
      
      console.log('Extracted items:', items);
      
      if (mountedRef.current) {
        setRows(items);
        setCurrentGroups([]);
        setShowingGroupItems(true);
        setCurrentGroupPath(groupPath);
        setTotalRowCount(totalCount);
        if (!groupNavigationHistory.includes(groupPath)) {
          setGroupNavigationHistory(prev => [...prev, groupPath]);
        }
        // Don't reset page - let pagination work naturally
        console.log('State updated - showing group items');
      }
      
    } catch (err) {
      console.error('Error in fetchGroupItems:', err);
      if (mountedRef.current) {
        setError(err as Error);
        dispatch(showAlert({ message: 'Failed to fetch group items', type: 'error' }));
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [apiEndpoint, onDataFetch, paginationModel.page, paginationModel.pageSize, sortModel, filterModel, debouncedSearchValue, dispatch, groupedUrl]);
  
  const fetchSubGroups = useCallback(async (parentGroup: GroupedDataRow) => {
    console.log('fetchSubGroups called for parent:', parentGroup.path);
    
    if ((!apiEndpoint && !onDataFetch) || !mountedRef.current) {
      console.log('Early return - missing apiEndpoint/onDataFetch or component unmounted');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      let subGroups: any[] = [];
      
      if (onDataFetch) {
        // Use onDataFetch for sub-groups
        console.log('Using onDataFetch for sub-groups');
        const params = {
          page: 0,
          pageSize: 100, // Get more sub-groups
          sortModel,
          filterModel,
          search: debouncedSearchValue,
          grouping: {
            GroupBy: groupingFields.map(gf => gf.field),
            Level: parentGroup.level + 1,
            GroupSortBy: 'key' as const,
            GroupSortDescending: false,
            IncludeCount: true,
            ParentKey: parentGroup.path,
          },
        };
        
        console.log('Calling onDataFetch with sub-group params:', params);
        const response = await onDataFetch(params);
        console.log('onDataFetch sub-groups response:', response);
        
        subGroups = Array.isArray(response.groups) ? response.groups : [];
        
      } else if (apiEndpoint) {
        // Use direct API endpoint for sub-groups
        console.log('Using direct API endpoint for sub-groups');
        const queryParams = new URLSearchParams({
          PageNumber: '1',
          PageSize: '100',
          Level: String(parentGroup.level + 1),
          ParentKey: parentGroup.path,
        });
        
        // Add all grouping fields
        groupingFields.forEach(field => {
          const mappedField = fieldMapper ? fieldMapper(field.field) : field.field;
          queryParams.append('GroupBy', mappedField);
        });
        
        const url = `${apiEndpoint}/grouped?${queryParams.toString()}`;
        console.log('Fetching sub-groups from URL:', url);
        
        const response = await DataService.get(url);
        console.log('Sub-groups response status:', response.status);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to fetch sub-groups`);
        }
        
        const jsonResponse = await response.json();
        console.log('Sub-groups response data:', jsonResponse);
        
        // Handle nested response structure
        if (jsonResponse?.data?.groups) {
          subGroups = Array.isArray(jsonResponse.data.groups) ? jsonResponse.data.groups : [];
        } else if (jsonResponse?.groups) {
          subGroups = Array.isArray(jsonResponse.groups) ? jsonResponse.groups : [];
        }
      }
      
      console.log('Extracted sub-groups:', subGroups);
      
      // Transform sub-groups and display them
      if (mountedRef.current && subGroups.length > 0) {
        const transformedSubGroups = subGroups.map((group: any, index: number) => ({
          id: `group-${parentGroup.level + 1}-${index}-${group.key}`,
          groupKey: String(group.key),
          groupValue: String(group.key),
          count: Number(group.count || 0),
          isGroup: true as const,
          level: parentGroup.level + 1,
          path: group.path || `${parentGroup.path}:${group.key}`,
          expanded: false,
          hasSubGroups: (parentGroup.level + 1) < groupingFields.length - 1,
        }));
        
        setCurrentGroups(transformedSubGroups);
        setRows([]);
        setCurrentGroupLevel(parentGroup.level + 1);
        setGroupNavigationHistory(prev => [...prev, parentGroup.path]);
        console.log('Sub-groups displayed');
      }
      
    } catch (err) {
      console.error('Error in fetchSubGroups:', err);
      if (mountedRef.current) {
        setError(err as Error);
        dispatch(showAlert({ message: 'Failed to fetch sub-groups', type: 'error' }));
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [apiEndpoint, onDataFetch, sortModel, filterModel, debouncedSearchValue, groupingFields, fieldMapper, dispatch]);
  
  
  const handleGroupExpand = useCallback(async (url: string, groupRow: GroupedDataRow) => {
    const conditionMet = enableGroupDrillDown && useBackendPagination && (apiEndpoint || onDataFetch);
    console.log('Drill-down condition met:', conditionMet);
    
    if (conditionMet) {
      saveGroupsSnapshot();
      // Reset pagination when entering a new group
      setPaginationModel(prev => ({ ...prev, page: 0 }));

      const isLastLevel = groupRow.level >= groupingFields.length - 1;
      console.log('isLastLevel calculation:', groupRow.level, '>=', groupingFields.length - 1, '=', isLastLevel);
      console.log('groupingFields:', groupingFields.map(gf => gf.field));

 if (isLastLevel) {
      await fetchGroupItems(url, groupRow.path);
    } else {
      await fetchSubGroups(groupRow);
    }
    return;
  }
  }, [enableGroupDrillDown, useBackendPagination, apiEndpoint, onDataFetch, groupingFields.length, fetchGroupItems, fetchSubGroups]);
  
  const handleBackToGroups = useCallback(() => {

    if (restoreGroupsSnapshot()) {
    return;
  }

    if (showingGroupItems) {
      setShowingGroupItems(false);
      setCurrentGroupPath(null);
      setRows([]);
      
      if (currentGroupLevel > 0 && groupNavigationHistory.length > 0) {
        // Go back to the parent group level
        const parentPath = groupNavigationHistory[groupNavigationHistory.length - 1];
        const parentGroup: GroupedDataRow = {
          id: 'temp-parent',
          groupKey: '',
          groupValue: '',
          count: 0,
          isGroup: true,
          level: currentGroupLevel - 2, // Parent of current level
          path: parentPath.split(':').slice(0, -1).join(':') || '', // Remove last part of path
          expanded: false,
        };
        
        if (currentGroupLevel === 1) {
          // Go back to root level
          fetchDataRef.current?.();
        } else {
          // Fetch parent level sub-groups
          fetchSubGroups(parentGroup);
        }
      } else {
        fetchDataRef.current?.();
      }
    } else if (currentGroupLevel > 0) {
      if (currentGroupLevel === 1) {
        setCurrentGroupLevel(0);
        setGroupNavigationHistory([]);
        fetchDataRef.current?.();
      } else {
        const newHistory = groupNavigationHistory.slice(0, -1);
        const parentPath = newHistory[newHistory.length - 1] || '';
        
        setCurrentGroupLevel(prev => prev - 1);
        setGroupNavigationHistory(newHistory);
        
        if (parentPath) {
          const parentGroup: GroupedDataRow = {
            id: 'temp-parent',
            groupKey: '',
            groupValue: '',
            count: 0,
            isGroup: true,
            level: currentGroupLevel - 2,
            path: parentPath,
            expanded: false,
          };
          fetchSubGroups(parentGroup);
        } else {
          fetchDataRef.current?.();
        }
      }
    }
  }, [showingGroupItems, currentGroupLevel, groupNavigationHistory, fetchSubGroups]);
  
  const fetchGroupData = useCallback(async (groupRow: GroupedDataRow) => {
    if (!useBackendPagination || !apiEndpoint) return;
    
    try {
      setIsLoading(true);
      
      const isLastLevel = groupRow.level >= groupingFields.length - 1;
      
      if (isLastLevel) {
        // Fetch items in this group
        const queryParams = new URLSearchParams({
          PageNumber: '1',
          PageSize: '50', // Adjust as needed
        });
        
        const encodedPath = encodeURIComponent(groupRow.path);
        const response = await DataService.get(`${apiEndpoint}/grouped/${encodedPath}?${queryParams.toString()}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch group items');
        }
        
        const jsonResponse = await response.json();
        const items = jsonResponse?.data?.items || jsonResponse?.items || [];
        
        setGroupCache(prev => ({
          ...prev,
          [groupRow.path]: {
            type: 'items',
            data: items,
            timestamp: Date.now(),
          }
        }));
        
      } else {
        // Fetch sub-groups
        const queryParams = new URLSearchParams({
          PageNumber: '1',
          PageSize: '100',
          Level: String(groupRow.level + 1),
          ParentKey: groupRow.path,
        });
        
        groupingFields.forEach(field => {
          queryParams.append('GroupBy', field.field);
        });
        
        const response = await DataService.get(`${apiEndpoint}/grouped?${queryParams.toString()}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch sub-groups');
        }
        
        const jsonResponse = await response.json();
        const subGroups = jsonResponse?.data?.groups || jsonResponse?.groups || [];
        
        setGroupCache(prev => ({
          ...prev,
          [groupRow.path]: {
            type: 'groups',
            data: subGroups,
            timestamp: Date.now(),
          }
        }));
      }
      
    } catch (error) {
      console.error('Error fetching group data:', error);
      dispatch(showAlert({ message: 'Failed to load group data', type: 'error' }));
    } finally {
      setIsLoading(false);
    }
  }, [useBackendPagination, apiEndpoint, groupingFields, dispatch]);
  
  // Use ref to store fetchData to avoid circular dependencies
  const fetchDataRef = useRef<() => Promise<void>>();
  const mountedRef = useRef(true);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);
  
  // Fetch data from backend
  const fetchData = useCallback(async () => {
    if (!useBackendPagination) return;

    const isGrouped = groupingFields.length > 0;
    // If we have a current group path and we're not showing items, we're fetching sub-groups
    const isSubGroupFetch = isGrouped && currentGroupPath && !showingGroupItems;

    const params = {
      page: isGrouped && !showingGroupItems ? groupPaginationModel.page : paginationModel.page,
      pageSize: isGrouped && !showingGroupItems ? groupPaginationModel.pageSize : paginationModel.pageSize,
      sortModel,
      filterModel,
      search: debouncedSearchValue,
      grouping: isGrouped ? {
        GroupBy: groupingFields.map(gf => gf.field),
        Level: isSubGroupFetch ? currentGroupLevel + 1 : 0,
        GroupSortBy: 'key' as const,
        GroupSortDescending: false,
        IncludeCount: true,
        ParentKey: isSubGroupFetch ? currentGroupPath : undefined,
      } : undefined,
    };
    
    const cacheKey = getCacheKey(params);
    
    // Check cache
    if (cache[cacheKey] && Date.now() - cache[cacheKey].timestamp < cacheTimeout) {
      const cachedData = cache[cacheKey];
      if (cachedData.isGrouped && isGrouped) {
        // For grouped view, don't show items in table
        if (Array.isArray(cachedData.data) && mountedRef.current) {
          const groupLevel = isSubGroupFetch ? currentGroupLevel + 1 : 0;
          const transformedGroups = cachedData.data
            .filter(group => group && group.key != null)
            .map((group: any, index: number) => {
              const groupPath = group.path ||
                (isSubGroupFetch && currentGroupPath
                  ? `${currentGroupPath}:${group.key}`
                  : `${groupingFields[0]?.field || 'unknown'}:${group.key}`);
              return {
                id: `group-${groupLevel}-${index}-${group.key}`,
                groupKey: String(group.key),
                groupValue: String(group.key),
                count: Number(group.count || 0),
                isGroup: true as const,
                level: groupLevel,
                path: groupPath,
                expanded: expandedGroups.has(groupPath),
                hasSubGroups: groupLevel < groupingFields.length - 1,
              };
            });
          setRows([]);
          setCurrentGroups(transformedGroups);
          if (isSubGroupFetch) {
            setCurrentGroupLevel(groupLevel);
          }
        }
      } else if (!cachedData.isGrouped && !isGrouped && mountedRef.current) {
        const items = Array.isArray(cachedData.data) ? cachedData.data : [];
        setRows(items);
        setCurrentGroups([]);
      } else {
        // Cache mismatch, fetch new data
      }
      if (isGrouped && !showingGroupItems) {
        setGroupTotalRowCount(cachedData.totalGroups || cachedData.totalCount);
      } else {
        setTotalRowCount(cachedData.totalCount);
      }
      return;
    }
    
    if (mountedRef.current) {
      setIsLoading(true);
      setError(null);
    }
    
    try {
      let response: BackendPaginationResponse;
      
      if (onDataFetch) {
        response = await onDataFetch(params);
      } else if (apiEndpoint) {
        const currentPage = isGrouped && !showingGroupItems ? groupPaginationModel.page : paginationModel.page;
        const currentPageSize = isGrouped && !showingGroupItems ? groupPaginationModel.pageSize : paginationModel.pageSize;
        const queryParams = new URLSearchParams({
          PageNumber: String(currentPage + 1),
          PageSize: String(currentPageSize),
        });
        
        // Add search param if exists
        if (debouncedSearchValue) {
          queryParams.append('SearchTerm', debouncedSearchValue);
          // Add searchable columns
          const searchableColumns = userColumns
            .filter(col => col.searchable !== false)
            .map(col => col.field);
          searchableColumns.forEach(field => {
            const mappedField = fieldMapper ? fieldMapper(field) : field;
            queryParams.append('SearchColumns', mappedField);
          });
        }
        
        // Add grouping params if grouped
        if (isGrouped && params.grouping) {
          params.grouping.GroupBy.forEach(field => {
            const mappedField = fieldMapper ? fieldMapper(field) : field;
            queryParams.append('GroupBy', mappedField);
          });
          queryParams.append('Level', String(params.grouping.Level));
          queryParams.append('GroupSortBy', params.grouping.GroupSortBy);
          queryParams.append('GroupSortDescending', String(params.grouping.GroupSortDescending));
          queryParams.append('IncludeCount', String(params.grouping.IncludeCount));
        }
        
        // Add sort params
        if (sortModel.length > 0) {
          const sortField = fieldMapper ? fieldMapper(sortModel[0].field) : sortModel[0].field;
          queryParams.append('SortBy', sortField);
          queryParams.append('SortDescending', String(sortModel[0].sort === 'desc'));
        }
        
        // Add filter params
        filterModel.items.forEach(filter => {
          if (filter.value !== undefined && filter.value !== '') {
            const mappedField = fieldMapper ? fieldMapper(filter.field) : filter.field;
            queryParams.append(`filters[${mappedField}]`, String(filter.value));
          }
        });
        
        const endpoint = isGrouped ? `${apiEndpoint}/grouped` : apiEndpoint;
        const apiResponse = await DataService.get(`${endpoint}?${queryParams.toString()}`);
        
        if (!apiResponse.ok) {
          throw new Error('Failed to fetch data');
        }
        
        const jsonResponse = await apiResponse.json();
        
        // Handle nested response structure
        if (jsonResponse.success && jsonResponse.data) {
          const data = jsonResponse.data;
          
          if (isGrouped) {
            // Handle grouped response
            const groups = data.groups || [];
            const currentPage = isGrouped && !showingGroupItems ? groupPaginationModel.page : paginationModel.page;
            const currentPageSize = isGrouped && !showingGroupItems ? groupPaginationModel.pageSize : paginationModel.pageSize;
            
            response = {
              groups: groups,
              totalCount: data.totalCount || groups.length,
              totalGroups: data.totalGroups || data.totalCount || groups.length,
              pageNumber: data.pageNumber || (currentPage + 1),
              pageSize: data.pageSize || currentPageSize,
              totalPages: data.totalPages || Math.ceil((data.totalGroups || data.totalCount || groups.length) / currentPageSize),
              hasNext: data.hasNext !== undefined ? data.hasNext : ((currentPage + 1) * currentPageSize < (data.totalGroups || data.totalCount || groups.length)),
              hasPrevious: data.hasPrevious !== undefined ? data.hasPrevious : (currentPage > 0),
            };
          } else {
            // Handle regular response
            let items: any[] = [];
            if (data.items) {
              if (Array.isArray(data.items)) {
                items = data.items;
              } else if (typeof data.items === 'object') {
                items = Object.values(data.items);
              }
            }
            
            response = {
              items: items,
              totalCount: data.totalCount || items.length,
              pageNumber: data.pageNumber || paginationModel.page + 1,
              pageSize: data.pageSize || paginationModel.pageSize,
              totalPages: data.totalPages || Math.ceil((data.totalCount || items.length) / paginationModel.pageSize),
              hasNext: data.hasNext || false,
              hasPrevious: data.hasPrevious || false,
            };
          }
        } else {
          // Fallback for direct response format
          response = jsonResponse;
        }
      } else {
        throw new Error('No API endpoint or fetch function provided');
      }
      
      let processedData: any;
      
      if (isGrouped && response.groups && Array.isArray(response.groups)) {
        // Transform grouped data inline to avoid circular dependency
        const groupLevel = isSubGroupFetch ? currentGroupLevel + 1 : 0;
        const transformedGroups = response.groups
          .filter(group => group && group.key != null) // Filter out invalid groups
          .map((group: any, index: number) => {
            // Use path from API if available, otherwise construct it
            const groupPath = group.path ||
              (isSubGroupFetch && currentGroupPath
                ? `${currentGroupPath}:${group.key}`
                : `${groupingFields[0]?.field || 'unknown'}:${group.key}`);
            return {
              id: `group-${groupLevel}-${index}-${group.key}`,
              groupKey: String(group.key),
              groupValue: String(group.key),
              count: Number(group.count || 0),
              isGroup: true as const,
              level: groupLevel,
              path: groupPath,
              expanded: expandedGroups.has(groupPath),
              hasSubGroups: groupLevel < groupingFields.length - 1,
            };
          });
        
        // Update cache
        if (mountedRef.current) {
          setCache(prev => ({
            ...prev,
            [cacheKey]: {
              data: response.groups,
              totalCount: response.totalCount,
              totalGroups: response.totalGroups,
              timestamp: Date.now(),
              isGrouped: true,
            },
          }));
        }
        
        // For grouped view, we don't show items in the table
        if (mountedRef.current) {
          setRows([]);
          setCurrentGroups(transformedGroups);
          // Update group level when fetching sub-groups
          if (isSubGroupFetch) {
            setCurrentGroupLevel(groupLevel);
          }
        }
      } else {
        // Regular data
        const items = Array.isArray(response.items) ? response.items : 
                     Array.isArray(response.data) ? response.data : [];
        
        // Update cache
        if (mountedRef.current) {
          setCache(prev => ({
            ...prev,
            [cacheKey]: {
              data: items,
              totalCount: response.totalCount,
              timestamp: Date.now(),
              isGrouped: false,
            },
          }));
          
          setRows(items);
          setCurrentGroups([]);
        }
      }
      
      if (mountedRef.current) {
        if (isGrouped && !showingGroupItems) {
          // For groups view, use totalGroups if available, otherwise totalCount
          setGroupTotalRowCount(response.totalGroups || response.totalCount);
        } else {
          // For items view (regular or group items)
          setTotalRowCount(response.totalCount);
        }
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err as Error);
        dispatch(showAlert({ message: 'Failed to fetch data', type: 'error' }));
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    useBackendPagination,
    paginationModel,
    sortModel,
    filterModel,
    apiEndpoint,
    onDataFetch,
    cacheTimeout,
    getCacheKey,
    cache,
    dispatch,
    debouncedSearchValue,
    userColumns,
    groupingFields,
    groupPaginationModel,
    showingGroupItems,
    currentGroupPath,
    currentGroupLevel,
    idField,
  ]);
  
  // Store fetchData in ref to avoid circular dependencies
  fetchDataRef.current = fetchData;
  
  // Clear cache when search, sort, filter, or grouping changes (not pagination)
  const prevSortRef = useRef(sortModel);
  const prevFilterRef = useRef(filterModel);
  const prevSearchRef = useRef(debouncedSearchValue);
  const prevGroupingRef = useRef(groupingFields);
  
  useEffect(() => {
    if (useBackendPagination) {
      const sortChanged = JSON.stringify(prevSortRef.current) !== JSON.stringify(sortModel);
      const filterChanged = JSON.stringify(prevFilterRef.current) !== JSON.stringify(filterModel);
      const searchChanged = prevSearchRef.current !== debouncedSearchValue;
      const groupingChanged = JSON.stringify(prevGroupingRef.current) !== JSON.stringify(groupingFields);
      
      if (sortChanged || filterChanged || searchChanged || groupingChanged) {
        setCache({});
        if (searchChanged || groupingChanged) {
          setPaginationModel(prev => ({ ...prev, page: 0 }));
        }
      }
      
      prevSortRef.current = sortModel;
      prevFilterRef.current = filterModel;
      prevSearchRef.current = debouncedSearchValue;
      prevGroupingRef.current = groupingFields;
    }
  }, [sortModel, filterModel, debouncedSearchValue, groupingFields, useBackendPagination]);
  
  const prevGroupPageRef = useRef<number>(-1);
  useEffect(() => {
    if (!showingGroupItems && groupingFields.length > 0 && useBackendPagination) {
      if (prevGroupPageRef.current === -1 || prevGroupPageRef.current !== groupPaginationModel.page) {
        console.log('Group pagination changed from', prevGroupPageRef.current, 'to', groupPaginationModel.page);
        fetchDataRef.current?.();
      }
      prevGroupPageRef.current = groupPaginationModel.page;
    }
  }, [groupPaginationModel.page, showingGroupItems, groupingFields.length, useBackendPagination]);

  const prevGroupItemPageRef = useRef<number>(-1);
  useEffect(() => {
    if (showingGroupItems && currentGroupPath && useBackendPagination) {
      if (prevGroupItemPageRef.current === -1 || prevGroupItemPageRef.current !== paginationModel.page) {
        console.log('Group item pagination changed from', prevGroupItemPageRef.current, 'to', paginationModel.page);
        fetchGroupItems(groupedUrl, currentGroupPath);
      }
      prevGroupItemPageRef.current = paginationModel.page;
    }
  }, [showingGroupItems, currentGroupPath, paginationModel.page, useBackendPagination, fetchGroupItems]);
  
const lastGroupsSnapshotRef = useRef<{
  groups: GroupedDataRow[];
  groupPaginationModel: GridPaginationModel;
  groupTotalRowCount: number;
  currentGroupLevel: number;
  groupNavigationHistory: string[];
} | null>(null);

const saveGroupsSnapshot = useCallback(() => {
  lastGroupsSnapshotRef.current = {
    groups: currentGroups,
    groupPaginationModel,
    groupTotalRowCount,
    currentGroupLevel,
    groupNavigationHistory: [...groupNavigationHistory],
  };
}, [currentGroups, groupPaginationModel, groupTotalRowCount, currentGroupLevel, groupNavigationHistory]);


  useEffect(() => {
    if (useBackendPagination && !showingGroupItems) {
      fetchDataRef.current?.();
    }
  }, [
    useBackendPagination,
    paginationModel.page, 
    paginationModel.pageSize, 
    sortModel, 
    filterModel, 
    debouncedSearchValue, 
    groupingFields,
    showingGroupItems
  ]);
  
  useEffect(() => {
    if (!useBackendPagination) {
      if (groupingFields.length > 0) {
        const groupedData = groupLocalData(localRows, groupingFields);
        const transformedGroups = groupedData.map((group: any, index: number) => {
          const groupPath = `${groupingFields[0]?.field}:${group.key || group.value}`;
          return {
            id: `group-0-${index}-${group.key || group.value}`,
            groupKey: group.key || groupingFields[0]?.field || '',
            groupValue: group.key || group.value,
            count: group.count,
            isGroup: true as const,
            level: 0,
            path: groupPath,
            expanded: expandedGroups.has(groupPath),
            hasSubGroups: groupingFields.length > 1,
          };
        });
        setRows([]);
        setCurrentGroups(transformedGroups);
      } else {
        setRows(localRows);
        setCurrentGroups([]);
      }
      setTotalRowCount(localRows.length);
    }
  }, [
    useBackendPagination, 
    localRows,
    groupingFields,
    expandedGroups
  ]);
  
  // Client-side grouping helper (memoized for performance)
  const groupLocalData = useCallback((data: any[], fields: GroupingField[]) => {
    if (fields.length === 0) return data;
    
    const grouped = data.reduce((acc, item) => {
      const groupKey = fields[0].field;
      const groupValue = item[groupKey];
      
      if (!acc[groupValue]) {
        acc[groupValue] = {
          key: groupKey,
          value: groupValue,
          count: 0,
          children: [],
        };
      }
      
      acc[groupValue].children.push(item);
      acc[groupValue].count++;
      
      return acc;
    }, {} as Record<string, any>);
    
    return Object.values(grouped);
  }, []);
  
  // Groups Display Component
  const GroupsDisplay: React.FC<{ groups: GroupedDataRow[] }> = ({ groups }) => {
    return (
      <Box>
        <Box sx={{ p: 2 }}>
          {groups.map((group) => (
            <Box
              key={group.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1,
                py: 0.75, // Smaller vertical padding
                mb: 0.5, // Smaller margin
                backgroundColor: theme.palette.background.default,
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                cursor: 'pointer',
                minHeight: 40, // Fixed smaller height
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
                pl: 1 + group.level * 1.5, // Smaller indentation
              }}
              onClick={(e) => {
                console.log('Click event triggered for group:', group);
                e.preventDefault();
                e.stopPropagation();
                handleGroupExpand(groupedUrl ,group);
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton size="small">
                  {group.expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                </IconButton>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {group.groupValue}
                </Typography>
              </Box>
              <Badge badgeContent={group.count} color="primary" />
            </Box>
          ))}
        </Box>
        
        {/* Group Pagination Controls */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          p: 2, 
          pt: 0,
          borderTop: `1px solid ${theme.palette.divider}` 
        }}>
          <Typography variant="body2" color="text.secondary">
            {groupTotalRowCount > 0 ? (
              `${groupPaginationModel.page * groupPaginationModel.pageSize + 1}-${Math.min(
                (groupPaginationModel.page + 1) * groupPaginationModel.pageSize,
                groupTotalRowCount
              )} of ${groupTotalRowCount} groups`
            ) : (
              'No groups found'
            )}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              disabled={groupPaginationModel.page === 0}
              onClick={() => setGroupPaginationModel(prev => ({ ...prev, page: prev.page - 1 }))}
            >
              Previous
            </Button>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mx: 1 }}>
              Page {groupPaginationModel.page + 1} of {Math.ceil(groupTotalRowCount / groupPaginationModel.pageSize)}
            </Typography>
            <Button
              size="small"
              disabled={(groupPaginationModel.page + 1) * groupPaginationModel.pageSize >= groupTotalRowCount}
              onClick={() => setGroupPaginationModel(prev => ({ ...prev, page: prev.page + 1 }))}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };
  
  // Process columns
  const processedColumns = useMemo<GridColDef[]>(() => {
    const cols: GridColDef[] = userColumns.map(col => {
      const baseCol: GridColDef = {
        ...col,
        flex: col.flex || (col.width ? 0 : 1),
        width: col.width,
        minWidth: col.minWidth || (col.width ? col.width : 100),
        maxWidth: col.maxWidth,
        resizable: col.resizable !== false,
        sortable: col.sortable !== false,
        groupable: col.groupable !== false, // Default to true unless explicitly set to false
        renderHeader: (params) => (
          <DraggableColumnHeader column={col} density={density}>
            {col.renderHeader ? col.renderHeader(params) : (col.headerName || col.field)}
          </DraggableColumnHeader>
        ),
      };
      
      // Special handling for grouped rows
      const originalRenderCell = baseCol.renderCell;
      baseCol.renderCell = (params) => {
        const row = params.row;
        
        // Groups are now handled in separate component, not in table rows
        
        // Handle regular rows with group indentation
        if (row.__groupLevel > 0) {
          return (
            <Box sx={{ pl: (row.__groupLevel + 1) * 2 }}>
              {originalRenderCell ? originalRenderCell(params) : params.value}
            </Box>
          );
        }
        
        // Default rendering
        return originalRenderCell ? originalRenderCell(params) : params.value;
      };
      
      // Handle custom column types
      switch (col.columnType) {
        case 'color':
          baseCol.renderCell = (params) => {
            const colorValue = colorParser(params.value);
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: colorValue,
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    borderRadius: wmsMode ? '2px' : '50%',
                  }}
                />
                <Typography variant="body2">{params.value}</Typography>
              </Box>
            );
          };
          break;
          
        case 'chip':
          baseCol.renderCell = (params) => {
            const chipStyle = col.chipColors?.[params.value];
            return (
              <Chip
                label={params.value}
                size={density.chipSize}
                style={{
                  backgroundColor: chipStyle?.backgroundColor,
                  color: chipStyle?.color,
                  fontSize: `${density.fontSize - 0.125}rem`,
                  height: density.chipSize === 'small' ? 20 : 24,
                }}
              />
            );
          };
          break;
          
        case 'progress':
          baseCol.renderCell = (params) => {
            const config = col.progressConfig!;
            const expected = params.row[config.expectedField];
            const actual = params.row[config.actualField];
            const dueDate = config.dueDateField ? params.row[config.dueDateField] : undefined;
            const percent = expected > 0 ? Math.min((actual / expected) * 100, 100) : 0;
            
            let color: 'success' | 'error' | 'warning' = 'success';
            if (dueDate && dayjs().isAfter(dayjs(dueDate))) {
              color = 'warning';
            } else if (actual > expected) {
              color = 'error';
            }
            
            return (
              <Box sx={{ width: '100%' }}>
                <LinearProgress
                  variant="determinate"
                  value={percent}
                  color={color}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {actual} / {expected}
                </Typography>
              </Box>
            );
          };
          break;
          
        case 'phone':
          baseCol.valueFormatter = (params) => formatIntPhoneNumber(params.value);
          break;
          
        case 'currency':
          baseCol.valueFormatter = (params) => formatCurrencyNumber(params.value);
          break;
      }
      
      // Handle date/datetime columns with improved formatting
      if (col.type === 'date') {
        baseCol.valueFormatter = (params) => {
          if (!params.value) return '';
          try {
            const date = new Date(params.value);
            if (isNaN(date.getTime())) return params.value;
            return formatDate(params.value);
          } catch {
            return params.value;
          }
        };
      } else if (col.type === 'dateTime') {
        baseCol.valueFormatter = (params) => {
          if (!params.value) return '';
          try {
            const date = new Date(params.value);
            if (isNaN(date.getTime())) return params.value;
            return formatDateTime(params.value);
          } catch {
            return params.value;
          }
        };
        
        // Add custom renderCell for dateTime to show both date and time in a structured way
        if (!baseCol.renderCell) {
          baseCol.renderCell = (params) => {
            if (!params.value) return '';
            try {
              const date = new Date(params.value);
              if (isNaN(date.getTime())) return params.value;
              
              // Format for ISO strings like "2025-09-03T12:08:44.846Z"
              const dateStr = date.toLocaleDateString('en-US', { 
                month: 'short', 
                day: '2-digit', 
                year: 'numeric' 
              });
              const timeStr = date.toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit',
                second: '2-digit',
                hour12: false 
              });
              
              return (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {dateStr}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {timeStr}
                  </Typography>
                </Box>
              );
            } catch {
              return params.value;
            }
          };
        }
      }
      
      // Auto-detect and format ISO date strings for columns without explicit renderCell
      if (!baseCol.renderCell && !col.type) {
        const originalRenderCell = baseCol.renderCell;
        baseCol.renderCell = (params) => {
          // Try to auto-format if it looks like an ISO date
          const autoFormattedDate = detectAndFormatDate(params.value);
          if (autoFormattedDate) {
            return autoFormattedDate;
          }
          
          // Fall back to original render or default
          return originalRenderCell ? originalRenderCell(params) : params.value;
        };
      }
      
      return baseCol;
    });
    
    // Add actions column if needed
    if (rowActions.length > 0 || onEditRow || onDeleteRow) {
      cols.push({
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 100,
        cellClassName: 'actions',
        getActions: ({ id, row }) => {
          const actions: React.ReactElement[] = [];
          
          if (onEditRow) {
            actions.push(
              <GridActionsCellItem
                icon={<EditIcon />}
                label="Edit"
                onClick={() => handleEditClick(id)}
                color="inherit"
              />
            );
          }
          
          if (onDeleteRow) {
            actions.push(
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={() => handleDeleteClick(id)}
                color="inherit"
              />
            );
          }
          
          rowActions
            .filter(action => !action.visible || action.visible(row))
            .forEach(action => {
              actions.push(
                <GridActionsCellItem
                  icon={React.isValidElement(action.icon) ? action.icon : <span>{action.icon}</span>}
                  label={action.label}
                  onClick={() => action.onClick(id, row)}
                  color={action.color || 'inherit'}
                  disabled={action.disabled?.(row)}
                />
              );
            });
          
          return actions;
        },
      });
    }
    
    return cols;
  }, [userColumns, rowActions, onEditRow, onDeleteRow, theme, wmsMode, handleGroupExpand]);
  
  // Handlers
  const handleEditClick = (id: GridRowId) => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };
  
  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };
  
  const handleDeleteClick = async (id: GridRowId) => {
    if (window.confirm('Are you sure you want to delete this row?')) {
      try {
        if (onDeleteRow) {
          await onDeleteRow(id);
          dispatch(showAlert({ message: 'Row deleted successfully', type: 'success' }));
        }
        setRows(rows.filter((row) => row[idField] !== id));
      } catch (error: any) {
        dispatch(showAlert({ message: error.message || 'Failed to delete row', type: 'error' }));
      }
    }
  };
  
  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };
  
  const processRowUpdate = async (newRow: GridValidRowModel) => {
    try {
      if (onEditRow) {
        const updatedRow = await onEditRow(newRow);
        dispatch(showAlert({ message: 'Row updated successfully', type: 'success' }));
        if (useBackendPagination) {
          fetchDataRef.current?.();
        }
        return updatedRow || newRow;
      }
      return newRow;
    } catch (error: any) {
      dispatch(showAlert({ message: error.message || 'Failed to update row', type: 'error' }));
      throw error;
    }
  };
  
  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };
  
  const handleAddRow = async () => {
    const newId = Math.max(...rows.map(r => r[idField] || 0)) + 1;
    const newRow = { [idField]: newId };
    
    userColumns.forEach(col => {
      if (col.field !== idField) {
        newRow[col.field] = col.type === 'number' ? 0 : undefined;
      }
    });
    
    setRows([newRow, ...rows]);
    setRowModesModel({
      ...rowModesModel,
      [newId]: { mode: GridRowModes.Edit, fieldToFocus: userColumns[0].field },
    });
  };
  
  const handleActionClick = async (action: PaginatedDataGridAction) => {
    if (action.confirmDialog) {
      const message = typeof action.confirmDialog.message === 'function'
        ? action.confirmDialog.message(selectionModel.length)
        : action.confirmDialog.message;
        
      if (!window.confirm(message)) return;
    }
    
    try {
      await action.onClick(selectionModel);
      setSelectionModel([]);
      if (useBackendPagination) {
        fetchDataRef.current?.();
      }
    } catch (error: any) {
      dispatch(showAlert({ message: error.message || 'Action failed', type: 'error' }));
    }
  };
  
  if (externalLoading && !rows.length) {
    return (
      <Paper className={cx(classes.container, className)} sx={sx}>
        <Box className={classes.loadingOverlay}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }
  
  if ((externalError || error) && !rows.length) {
    const currentError = externalError || error;
    return (
      <Paper className={cx(classes.container, className)} sx={sx}>
        <Alert
          severity="error"
          className={classes.errorState}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => fetchDataRef.current?.()}
            >
              Retry
            </Button>
          }
        >
          <AlertTitle>Error Loading Data</AlertTitle>
          {currentError?.message || 'An unexpected error occurred'}
        </Alert>
      </Paper>
    );
  }
  
  const loading = useBackendPagination ? isLoading : externalLoading;

  // Enhanced dynamic height calculation
  const calculateDynamicHeight = useCallback(() => {
    if (autoHeight) return 'auto';
    if (maxHeight) return maxHeight;
    
    const rowCount = rows.length;
    const isGrouped = groupingFields.length > 0 && !showingGroupItems;
    const effectiveRowCount = isGrouped ? currentGroups.length : rowCount;
    
    // Enhanced empty state handling
    if (effectiveRowCount === 0) {
      const emptyStateHeight = compactEmptyState ? 120 : 200;
      return Math.max(emptyStateHeight, minHeight as number || emptyStateHeight);
    }
    
    // Accurate measurements based on density
    const showToolbar = actions.length > 0 || onAddRow || (useBackendPagination && searchValue !== undefined) || groupingFields.length > 0;
    const toolbarHeight = showToolbar ? 56 : 0;
    const headerHeight = density.rowHeight + density.headerPadding * 2; // More accurate header height
    const showPagination = useBackendPagination || effectiveRowCount > (paginationModel.pageSize || 10);
    const paginationHeight = showPagination && !hideFooterWhenEmpty ? 52 : 0;
    const borderPadding = 2;
    const maxVisibleRows = paginationModel.pageSize || 10;
    
    // Calculate height for visible rows with better density handling
    const visibleRows = Math.min(effectiveRowCount, maxVisibleRows);
    const rowsHeight = visibleRows * density.rowHeight;
    const calculatedHeight = toolbarHeight + headerHeight + rowsHeight + paginationHeight + borderPadding;
    
    // Intelligent minimum height based on content
    const minimumHeight = Math.max(
      minHeight as number || 150,
      toolbarHeight + headerHeight + density.rowHeight + borderPadding
    );
    
    return Math.max(calculatedHeight, minimumHeight);
  }, [
    autoHeight,
    maxHeight,
    minHeight,
    rows.length,
    currentGroups.length,
    groupingFields.length,
    showingGroupItems,
    density,
    compactEmptyState,
    actions.length,
    onAddRow,
    searchValue,
    paginationModel.pageSize,
    useBackendPagination,
    hideFooterWhenEmpty
  ]);

  // Enhanced empty state renderer
  const renderEmptyState = () => {
    const hasFilters = searchValue || (filterModel.items && filterModel.items.length > 0);
    const emptyStateClass = compactEmptyState ? 
      cx(classes.emptyState, classes.compactEmptyState) : 
      classes.emptyState;
    
    const iconSize = compactEmptyState ? 40 : 64;
    const buttonSize = compactEmptyState ? 'small' : 'medium';
    
    return (
      <Paper className={emptyStateClass} elevation={compactEmptyState ? 1 : 3}>
        <ErrorOutline 
          sx={{ 
            fontSize: iconSize, 
            mb: compactEmptyState ? 1 : 2, 
            color: theme.palette.primary.light, 
            opacity: 0.7 
          }} 
        />
        <Typography 
          variant={compactEmptyState ? "subtitle1" : "h5"} 
          gutterBottom 
          color="text.primary" 
          sx={{ fontWeight: 600 }}
        >
          {emptyMessage}
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mb: compactEmptyState ? 1.5 : 3, 
            textAlign: 'center',
            fontSize: compactEmptyState ? '0.8rem' : undefined
          }}
        >
          {hasFilters
            ? "Try adjusting your search or filters."
            : "There is currently no data to display here."}
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          gap: compactEmptyState ? 1 : 2,
          flexWrap: 'wrap',
          justifyContent: 'center'
        }}>
          {hasFilters && (
            <Button
              variant="outlined"
              size={buttonSize}
              onClick={() => {
                setSearchValue('');
                setFilterModel({ items: [] });
              }}
            >
              Clear Filters
            </Button>
          )}
          {onAddRow && (
            <Button
              variant="contained"
              size={buttonSize}
              startIcon={<AddIcon sx={{ fontSize: `${density.iconSize}px !important` }} />}
              onClick={handleAddRow}
            >
              Add New
            </Button>
          )}
        </Box>
      </Paper>
    );
  };
  
  return (
    <Paper
      className={cx(
        classes.container,
        wmsMode && classes.wmsContainer,
        className
      )}
      sx={sx}
    >
      {(title || subtitle) && (
        <Box
          className={cx(
            classes.header,
            collapsible && classes.headerClickable
          )}
          onClick={collapsible ? () => setCollapsed(!collapsed) : undefined}
        >
          <Box className={classes.titleSection}>
            {title && <Typography className={classes.title}>{title}</Typography>}
            {subtitle && <Typography className={classes.subtitle}>{subtitle}</Typography>}
          </Box>
          <Box className={classes.actions}>
            {collapsible && (
              <IconButton size="small">
                {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </IconButton>
            )}
          </Box>
        </Box>
      )}
      
      {!collapsed && (
        <>
          {/* Grouping Zone */}
          {enableGrouping && (
            <GroupingZone
              groupingFields={groupingFields}
              availableColumns={userColumns.filter(col => col.groupable !== false)}
              onGroupingChange={handleGroupingChange}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragLeave={handleDragLeave}
              isDragOver={isDragOver}
              density={density}
            />
          )}
          
          {/* Back to Groups button */}
          {(showingGroupItems || currentGroupLevel > 0) && enableGroupDrillDown && (
            <Box sx={{ p: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Button
                startIcon={<KeyboardArrowLeftIcon />}
                onClick={handleBackToGroups}
                size="small"
                variant="outlined"
              >
                {showingGroupItems 
                  ? (currentGroupLevel > 0 ? `Back to ${groupingFields[currentGroupLevel - 1]?.headerName || 'Groups'}` : 'Back to Groups')
                  : (currentGroupLevel === 1 ? 'Back to Groups' : `Back to ${groupingFields[currentGroupLevel - 2]?.headerName || 'Parent Level'}`)
                }
              </Button>
            </Box>
          )}
          
          {groupingFields.length > 0 && !showingGroupItems ? (
            // Groups View
            currentGroups.length === 0 && !loading ? (
              <Box
                className={cx(
                  responsiveColumns && classes.responsiveContainer,
                  autoHeight && classes.adaptiveHeight
                )}
                sx={{
                  height: calculateDynamicHeight(),
                  width: '100%',
                  minHeight: minHeight || 'auto'
                }}
              >
                <DataGrid
                  rows={[]}
                  columns={processedColumns}
                  loading={loading}
                  rowCount={0}
                  pageSizeOptions={[5, 10, 25, 50, 100]}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  paginationMode={useBackendPagination ? 'server' : 'client'}
                  sortingMode={useBackendPagination ? 'server' : 'client'}
                  filterMode={useBackendPagination ? 'server' : 'client'}
                  onSortModelChange={setSortModel}
                  onFilterModelChange={setFilterModel}
                  checkboxSelection
                  disableRowSelectionOnClick
                  rowSelectionModel={selectionModel}
                  onRowSelectionModelChange={(newSelection) => {
                    setSelectionModel(newSelection);
                    onSelectionChange?.(newSelection);
                  }}
                  density={getMuiDensityLevel(densityLevel)}
                  getRowId={(row) => row[idField]}
                  className={cx(
                    classes.dataGrid,
                    wmsMode && classes.wmsDataGrid,
                    striped && 'striped-rows'
                  )}
                  initialState={initialState}
                  slots={{
                    toolbar: (actions.length > 0 || onAddRow || (useBackendPagination && searchValue !== undefined) || groupingFields.length > 0) ? CustomToolbar : undefined,
                    noRowsOverlay: () => renderEmptyState(),
                  }}
                  slotProps={{
                    toolbar: {
                      actions,
                      selectedRows: selectionModel,
                      onActionClick: handleActionClick,
                      onAddClick: onAddRow ? handleAddRow : undefined,
                      canAdd: !!onAddRow,
                      searchValue: searchValue,
                      onSearchChange: useBackendPagination ? setSearchValue : undefined,
                      hasGrouping: groupingFields.length > 0,
                      onClearGrouping: handleClearGrouping,
                    } as any,
                  }}
                  hideFooter={hideFooterWhenEmpty}
                  autoHeight={autoHeight}
                  sx={{
                    [`& .${gridClasses.row}.striped-rows:nth-of-type(even)`]: {
                      backgroundColor: theme.palette.mode === 'dark' ? '#ffffff05' : '#00000005',
                    },
                    '& .MuiDataGrid-virtualScroller': {
                      minHeight: '60px',
                    },
                    '& .MuiDataGrid-footerContainer': {
                      minHeight: density.rowHeight,
                      padding: `${density.spacing * 4}px ${density.cellPadding}px`,
                    },
                    '& .MuiTablePagination-root': {
                      fontSize: `${density.fontSize}rem`,
                    },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      fontSize: `${Math.max(density.fontSize - 0.0625, 0.75)}rem`,
                    },
                    '& .MuiChip-root': {
                      fontSize: `${density.fontSize - 0.125}rem`,
                      height: density.chipSize === 'small' ? 20 : 24,
                    },
                    ...sx,
                  }}
                />
              </Box>
            ) : (
              <GroupsDisplay groups={currentGroups} />
            )
          ) : (
            <Box
              className={cx(
                responsiveColumns && classes.responsiveContainer,
                autoHeight && classes.adaptiveHeight
              )}
              sx={{
                height: calculateDynamicHeight(),
                width: '100%',
                minHeight: minHeight || 'auto'
              }}
            >
              <DataGrid
                rows={rows}
                columns={processedColumns}
                loading={loading}
                rowCount={useBackendPagination ? totalRowCount : undefined}
                pageSizeOptions={[5, 10, 25, 50, 100]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                paginationMode={useBackendPagination ? 'server' : 'client'}
                sortingMode={useBackendPagination ? 'server' : 'client'}
                filterMode={useBackendPagination ? 'server' : 'client'}
                onSortModelChange={setSortModel}
                onFilterModelChange={setFilterModel}
                checkboxSelection
                disableRowSelectionOnClick
                rowSelectionModel={selectionModel}
                onRowSelectionModelChange={(newSelection) => {
                  setSelectionModel(newSelection);
                  onSelectionChange?.(newSelection);
                }}
                editMode="row"
                rowModesModel={rowModesModel}
                onRowModesModelChange={setRowModesModel}
                onRowEditStop={handleRowEditStop}
                processRowUpdate={processRowUpdate}
                onProcessRowUpdateError={(error) => {
                  console.error('Row update error:', error);
                }}
                onRowClick={(params) => {
                  // Don't navigate for group rows or group items
                  if (params.row.isGroup || params.row.__isGroupItem) {
                    return;
                  }
                  onRowClick?.(params);
                }}
                density={getMuiDensityLevel(densityLevel)}
                getRowId={(row) => row.isGroup ? row.id : row[idField]}
                isRowSelectable={(params) => !params.row.isGroup}
                className={cx(
                  classes.dataGrid,
                  wmsMode && classes.wmsDataGrid,
                  striped && 'striped-rows'
                )}
                getRowClassName={(params) => {
                  if (params.row.isGroup) {
                    return classes.groupRowCell;
                  }
                  return '';
                }}
                initialState={initialState}
                slots={{
                  toolbar: (actions.length > 0 || onAddRow || (useBackendPagination && searchValue !== undefined) || groupingFields.length > 0) ? CustomToolbar : undefined,
                  noRowsOverlay: () => renderEmptyState(),
                }}
                slotProps={{
                  toolbar: {
                    actions,
                    selectedRows: selectionModel,
                    onActionClick: handleActionClick,
                    onAddClick: onAddRow ? handleAddRow : undefined,
                    canAdd: !!onAddRow,
                    searchValue: searchValue,
                    onSearchChange: useBackendPagination ? setSearchValue : undefined,
                    hasGrouping: groupingFields.length > 0,
                    onClearGrouping: handleClearGrouping,
                  } as any,
                }}
                hideFooter={(hideFooterWhenEmpty && rows.length === 0) || (rows.length <= (paginationModel.pageSize || 10) && !useBackendPagination)}
                autoHeight={autoHeight}
                sx={{
                  [`& .${gridClasses.row}.striped-rows:nth-of-type(even)`]: {
                    backgroundColor: theme.palette.mode === 'dark' ? '#ffffff05' : '#00000005',
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    minHeight: rows.length > 0 ? 'auto' : '60px',
                  },
                  '& .MuiDataGrid-footerContainer': {
                    minHeight: density.rowHeight,
                    padding: `${density.spacing * 4}px ${density.cellPadding}px`,
                  },
                  '& .MuiTablePagination-root': {
                    fontSize: `${density.fontSize}rem`,
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: `${Math.max(density.fontSize - 0.0625, 0.75)}rem`,
                  },
                  '& .MuiChip-root': {
                    fontSize: `${density.fontSize - 0.125}rem`,
                    height: density.chipSize === 'small' ? 20 : 24,
                  },
                  ...sx,
                }}
              />
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default PaginatedDataGrid;

export { colorParser };